// ItemLifecycleWarranty.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import Skeleton from 'react-loading-skeleton';
import CountUp from 'react-countup';
import {
  FaDownload,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaSortUp,
  FaSortDown,
  FaSearch,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';

import { Tooltip as ReactTooltip } from 'react-tooltip'; 
import { motion, AnimatePresence } from 'framer-motion';
import { CSVLink } from 'react-csv';

// CSS Imports
import 'react-datepicker/dist/react-datepicker.css';
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-tooltip/dist/react-tooltip.css';

ChartJS.register(
  ArcElement, LineElement, PointElement, LinearScale, 
  CategoryScale, BarElement, Title, ChartTooltip, Legend, Filler
);

const ItemLifecycleWarranty = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([null, null]);
  const [globalFilter, setGlobalFilter] = useState('');
  
  const [data, setData] = useState({
    stats: {},
    warrantyPie: {},
    serviceTrend: {},
    items: [],
  });

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      const mockData = {
        stats: {
          totalItems: 342,
          active: 218,
          expired: 47,
          expiringSoon: 38,
          avgDurationMonths: 21,
          insuranceCoverage: 94,
        },
        warrantyPie: { active: 82, expired: 18 },
        serviceTrend: {
          labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          values: [12, 18, 15, 22, 28, 35, 31, 27, 24, 19, 16, 21],
        },
        items: [
          { id: 1, name: 'Dell Latitude 7420', serial: 'SN-8X9K2P4', warrantyEnd: '2026-05-12', insurance: 'Active', stage: 'In Use', remainingDays: 122 },
          { id: 2, name: 'HP LaserJet Pro', serial: 'SN-P7M4N8Q', warrantyEnd: '2026-01-08', insurance: 'Expired', stage: 'Maintenance', remainingDays: 0 },
          { id: 3, name: 'Cisco Catalyst 9300', serial: 'SN-C9J5R1T', warrantyEnd: '2025-12-20', insurance: 'Active', stage: 'Deprecated', remainingDays: -21 },
          { id: 4, name: 'LG UltraWide Monitor', serial: 'SN-M3K8V2W', warrantyEnd: '2026-04-30', insurance: 'Active', stage: 'In Use', remainingDays: 110 },
          { id: 5, name: 'Ubiquiti Dream Machine Pro', serial: 'SN-U6L9F3X', warrantyEnd: '2026-02-15', insurance: 'Pending', stage: 'In Use', remainingDays: 36 },
        ],
      };
      setData(mockData);
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [dateRange]);

  const columns = useMemo(() => [
    { header: 'Item Name', accessorKey: 'name' },
    { header: 'Serial Number', accessorKey: 'serial' },
    { header: 'Warranty End', accessorKey: 'warrantyEnd' },
    { header: 'Insurance', accessorKey: 'insurance' },
    { header: 'Stage', accessorKey: 'stage' },
    {
      header: 'Warranty Progress',
      accessorKey: 'remainingDays',
      cell: ({ getValue }) => {
        const days = getValue();
        const { progress, color, icon } = getWarrantyProgress(days);
        return (
          <div className="flex items-center gap-3 font-roboto">
            <div className="w-24 bg-gray-200 rounded-full h-1.5 overflow-hidden hidden md:block">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                className={`h-full ${color}`}
              />
            </div>
            <span className="text-xs font-semibold min-w-[60px]">
              {days > 0 ? `${days}d left` : 'Expired'}
            </span>
            <span data-tooltip-id="warranty-tooltip" data-tooltip-content={days > 30 ? 'Healthy' : 'Action Required'}>
              {icon}
            </span>
          </div>
        );
      },
    },
  ], []);

  const table = useReactTable({
    data: data.items,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const getWarrantyProgress = (days) => {
    if (days < 0) return { progress: 100, color: 'bg-purple-600', icon: <FaTimesCircle className="text-purple-600" /> };
    if (days <= 30) return { progress: (days/30)*100, color: 'bg-red-500', icon: <FaExclamationTriangle className="text-red-500" /> };
    return { progress: Math.min(100, (days/365)*100), color: 'bg-green-500', icon: <FaCheckCircle className="text-green-500" /> };
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-10 font-roboto text-slate-700">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-10">
        <div>
          <h1 className="text-3xl font-poppins font-bold text-slate-900 tracking-tight">Lifecycle & Warranty</h1>
          <p className="text-sm font-roboto text-slate-500 mt-1">Enterprise Asset Integrity Dashboard</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 font-poppins">
          <DatePicker
            selectsRange
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            onChange={(update) => setDateRange(update)}
            className="border border-slate-200 rounded-xl px-4 py-2 text-sm bg-white shadow-sm outline-none focus:ring-2 focus:ring-blue-100"
            placeholderText="Filter by Date Range"
          />
          <CSVLink data={data.items} filename="warranty_report.csv">
            <button className="bg-slate-900 text-white text-xs font-bold uppercase tracking-widest px-6 py-2.5 rounded-xl hover:bg-slate-800 flex items-center gap-2 transition-all shadow-lg shadow-slate-200">
              <FaDownload /> Export CSV
            </button>
          </CSVLink>
        </div>
      </div>

      {/* KPI Section */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 mb-10">
        {[
          { label: 'Active Assets', val: data.stats.active, color: 'text-emerald-600' },
          { label: 'Expired Items', val: data.stats.expired, color: 'text-purple-600' },
          { label: 'Urgent Action', val: data.stats.expiringSoon, color: 'text-rose-500' },
          { label: 'Avg Life Span', val: data.stats.avgDurationMonths, suffix: 'mo', color: 'text-blue-600' },
          { label: 'Insured Rate', val: data.stats.insuranceCoverage, suffix: '%', color: 'text-teal-600' },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <p className="text-[10px] font-poppins font-black uppercase text-slate-400 tracking-[0.15em] mb-2">{stat.label}</p>
            <div className={`text-3xl font-poppins font-bold ${stat.color}`}>
              <CountUp end={stat.val || 0} suffix={stat.suffix || ''} />
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm h-[400px]">
          <h3 className="text-xs font-poppins font-black uppercase text-slate-400 tracking-widest mb-6">Warranty Health</h3>
          <div className="h-64">
             <Pie 
               data={{
                 labels: ['Active', 'Expired'],
                 datasets: [{
                   data: [data.warrantyPie.active || 0, data.warrantyPie.expired || 0],
                   backgroundColor: ['#10B981', '#8B5CF6'],
                   borderWidth: 0,
                 }],
               }} 
               options={{ maintainAspectRatio: false, plugins: { legend: { labels: { font: { family: 'Poppins', size: 10, weight: 'bold' } } } } }} 
             />
          </div>
        </div>
        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm h-[400px] lg:col-span-2">
          <h3 className="text-xs font-poppins font-black uppercase text-slate-400 tracking-widest mb-6">Service Request Trends</h3>
          <div className="h-64">
            <Line 
                data={{
                labels: data.serviceTrend.labels,
                datasets: [{ 
                    label: 'Requests', 
                    data: data.serviceTrend.values, 
                    borderColor: '#3b82f6', 
                    fill: true, 
                    backgroundColor: 'rgba(59,130,246,0.05)',
                    tension: 0.4,
                    pointRadius: 4,
                }]
                }} 
                options={{ maintainAspectRatio: false, scales: { x: { ticks: { font: { family: 'Roboto', size: 11 } } } } }} 
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-8 py-6 border-b border-slate-50 flex flex-col md:flex-row justify-between gap-4">
          <h3 className="font-poppins font-bold text-slate-800">Asset Registry</h3>
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
            <input
              value={globalFilter ?? ''}
              onChange={e => setGlobalFilter(e.target.value)}
              placeholder="Search assets..."
              className="pl-12 pr-4 py-2 bg-slate-50 border-none rounded-xl text-sm w-full md:w-80 outline-none focus:ring-2 focus:ring-blue-100 font-roboto"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left font-roboto">
            <thead className="bg-slate-50/50 text-[10px] font-poppins font-black uppercase text-slate-400 tracking-widest">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th 
                      key={header.id} 
                      className="px-8 py-5 cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-2">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{ asc: <FaSortUp />, desc: <FaSortDown /> }[header.column.getIsSorted()] ?? null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-50">
              <AnimatePresence>
                {table.getRowModel().rows.map(row => (
                    <motion.tr 
                        key={row.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-slate-50/50 transition-colors"
                    >
                    {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-8 py-4 text-xs font-medium text-slate-600">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    ))}
                    </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-8 py-6 bg-slate-50/30 flex items-center justify-between border-t border-slate-100 font-poppins">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <div className="flex gap-2">
            <button 
              onClick={() => table.previousPage()} 
              disabled={!table.getCanPreviousPage()}
              className="p-2.5 bg-white rounded-xl border border-slate-200 disabled:opacity-30 hover:border-blue-400 transition-all shadow-sm"
            >
              <FaChevronLeft size={10} />
            </button>
            <button 
              onClick={() => table.nextPage()} 
              disabled={!table.getCanNextPage()}
              className="p-2.5 bg-white rounded-xl border border-slate-200 disabled:opacity-30 hover:border-blue-400 transition-all shadow-sm"
            >
              <FaChevronRight size={10} />
            </button>
          </div>
        </div>
      </div>

      <ReactTooltip id="warranty-tooltip" style={{ fontFamily: 'Poppins', fontSize: '10px', fontWeight: 'bold' }} />
    </div>
  );
};

export default ItemLifecycleWarranty;